import React, { useState } from "react";
import {
    Alert,
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DeleteOutline as DeleteIcon, EditOutlined as EditIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import TableCommon from "../../Common/Table";
import { SearchRecords } from "../../Common/SearchRecords";
import Button from "../../Common/Button";
import { setSnackBar } from "../../../store/common/commonSlice";
import {
    useCreateMilestoneTierMutation,
    useDeleteMilestoneTierMutation,
    useGetMilestoneRewardsQuery,
    useGetMilestoneTiersQuery,
    useUpdateMilestoneRewardMutation,
    useUpdateMilestoneTierMutation,
} from "../../../store/shopAdmin/shopAdminApis";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { rupees } from "./affiliateUtils";

const EMPTY_TIER = { order_count: "", gift_name: "", prize_amount: "", is_active: true };

const TierDialog = ({ tier, onClose }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState(() =>
        tier?.id ? { ...tier, prize_amount: tier.prize_amount ?? "" } : { ...EMPTY_TIER },
    );
    const [create, { isLoading: creating }] = useCreateMilestoneTierMutation();
    const [update, { isLoading: updating }] = useUpdateMilestoneTierMutation();
    const set = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const save = async () => {
        const body = {
            order_count: Number(form.order_count),
            gift_name: String(form.gift_name || "").trim(),
            prize_amount: form.prize_amount === "" || form.prize_amount === null ? null : Number(form.prize_amount),
            is_active: Boolean(form.is_active),
        };
        if (!Number.isInteger(body.order_count) || body.order_count < 1) {
            return dispatch(
                setSnackBar({ open: true, message: "Enter the number of orders (1 or more)", severity: "error" }),
            );
        }
        if (body.gift_name.length < 2)
            return dispatch(setSnackBar({ open: true, message: "Enter the gift name", severity: "error" }));
        try {
            if (tier?.id) await update({ id: tier.id, ...body }).unwrap();
            else await create(body).unwrap();
            dispatch(setSnackBar({ open: true, message: tier?.id ? "Tier saved" : "Tier added", severity: "success" }));
            onClose();
        } catch (e) {
            dispatch(
                setSnackBar({ open: true, message: e?.data?.message || "Could not save the tier", severity: "error" }),
            );
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{tier?.id ? "Edit tier" : "Add tier"}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        type="number"
                        label="Delivered orders needed"
                        value={form.order_count}
                        onChange={set("order_count")}
                        autoFocus
                    />
                    <TextField
                        label="Gift name"
                        value={form.gift_name}
                        onChange={set("gift_name")}
                        inputProps={{ maxLength: 150 }}
                    />
                    <TextField
                        type="number"
                        label="Prize amount ₹ (optional)"
                        value={form.prize_amount}
                        onChange={set("prize_amount")}
                    />
                    <FormControlLabel
                        control={<Switch checked={Boolean(form.is_active)} onChange={set("is_active")} />}
                        label="Active (shown in the app)"
                    />
                    {tier?.id && tier.unlocked ? (
                        <Alert severity="info">
                            {tier.unlocked} customer{tier.unlocked > 1 ? "s have" : " has"} already unlocked this tier.
                            Their gifts stay as they are.
                        </Alert>
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button fullWidth={false} variant="text" onClick={onClose}>
                    Cancel
                </Button>
                <Button fullWidth={false} variant="contained" onClick={save} loading={creating || updating}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const TiersCard = () => {
    const dispatch = useDispatch();
    const { data: tiers = [], isFetching, error } = useGetMilestoneTiersQuery();
    const [remove] = useDeleteMilestoneTierMutation();
    const [editing, setEditing] = useState(null);

    const handleDelete = async (tier) => {
        if (
            !window.confirm(
                `Remove the ${tier.order_count}-order tier (${tier.gift_name})? Gifts already unlocked stay.`,
            )
        )
            return;
        try {
            await remove(tier.id).unwrap();
            dispatch(setSnackBar({ open: true, message: "Tier removed", severity: "success" }));
        } catch (e) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: e?.data?.message || "Could not remove the tier",
                    severity: "error",
                }),
            );
        }
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        Milestone tiers
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        A customer unlocks a gift when their own delivered app orders reach the count. Tiers are listed
                        by count. Changes show in the app straight away, with no app update.
                    </Typography>
                </Box>
                <Button
                    fullWidth={false}
                    variant="contained"
                    onClick={() => setEditing({})}
                    sx={{ whiteSpace: "nowrap", ml: 2 }}
                >
                    Add tier
                </Button>
            </Stack>
            {error ? <Alert severity="error">{error?.data?.message || "Failed to load tiers"}</Alert> : null}
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Orders</TableCell>
                        <TableCell>Gift</TableCell>
                        <TableCell align="right">Prize</TableCell>
                        <TableCell align="center">Shown in app</TableCell>
                        <TableCell align="right">Unlocked</TableCell>
                        <TableCell align="right" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tiers.map((t) => (
                        <TableRow key={t.id}>
                            <TableCell>
                                <strong>{t.order_count}</strong>
                            </TableCell>
                            <TableCell>{t.gift_name}</TableCell>
                            <TableCell align="right">
                                {t.prize_amount === null ? "-" : rupees(t.prize_amount)}
                            </TableCell>
                            <TableCell align="center">
                                <Chip
                                    size="small"
                                    color={t.is_active ? "success" : "default"}
                                    label={t.is_active ? "Yes" : "Hidden"}
                                />
                            </TableCell>
                            <TableCell align="right">{t.unlocked}</TableCell>
                            <TableCell align="right">
                                <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => setEditing(t)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove">
                                    <IconButton size="small" onClick={() => handleDelete(t)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                    {!isFetching && !tiers.length ? (
                        <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 3 }}>
                                No tiers yet. Add one to show a progress bar in the app.
                            </TableCell>
                        </TableRow>
                    ) : null}
                </TableBody>
            </Table>
            {editing ? <TierDialog tier={editing} onClose={() => setEditing(null)} /> : null}
        </Paper>
    );
};

const giftColumns = [
    { title: "Customer", field: "customer", sort: false, width: "22%" },
    { title: "Tier", field: "tier_order_count", sort: false, width: "10%" },
    { title: "Gift", field: "gift_name", sort: false, width: "22%" },
    { title: "Unlocked", field: "unlocked_at", sort: false, width: "14%" },
    { title: "Status", field: "status", sort: false, width: "12%", align: "center" },
    { title: "Note", field: "note", sort: false, width: "20%" },
];

const GiftsCard = () => {
    const dispatch = useDispatch();
    const [pagination, setPagination] = useState({
        pageNo: 0,
        limit: 10,
        sortBy: "DESC",
        sortField: "unlocked_at",
        keywords: "",
    });
    const [status, setStatus] = useState("all");
    const { data, isFetching, error } = useGetMilestoneRewardsQuery({
        page: pagination.pageNo + 1,
        limit: pagination.limit,
        keywords: pagination.keywords,
        status,
    });
    const [updateGift] = useUpdateMilestoneRewardMutation();

    const changeStatus = async (gift, next) => {
        const note =
            next === "sent" ? window.prompt("Note (optional), e.g. courier and reference", gift.note || "") : gift.note;
        if (note === null) return;
        try {
            await updateGift({ id: gift.id, status: next, note }).unwrap();
            dispatch(
                setSnackBar({
                    open: true,
                    message: next === "sent" ? "Marked as sent" : "Marked as pending",
                    severity: "success",
                }),
            );
        } catch (e) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: e?.data?.message || "Could not update the gift",
                    severity: "error",
                }),
            );
        }
    };

    const rows =
        !isFetching && data?.results?.length
            ? data.results.map((g) => (
                  <TableRow key={g.id} hover sx={{ "td, th": { border: 0, padding: "14px 12px" } }}>
                      <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                              {g.customer?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                              {g.customer?.mobile}
                          </Typography>
                      </TableCell>
                      <TableCell>{g.tier_order_count} orders</TableCell>
                      <TableCell>
                          {g.gift_name}
                          {g.prize_amount !== null ? (
                              <Typography variant="caption" display="block" color="text.secondary">
                                  {rupees(g.prize_amount)}
                              </Typography>
                          ) : null}
                      </TableCell>
                      <TableCell>{handleDateTimeDefault(g.unlocked_at)}</TableCell>
                      <TableCell align="center">
                          <TextField
                              select
                              size="small"
                              value={g.status}
                              onChange={(e) => changeStatus(g, e.target.value)}
                              sx={{ minWidth: 120 }}
                          >
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="sent">Sent</MenuItem>
                          </TextField>
                      </TableCell>
                      <TableCell>
                          <Typography variant="body2">{g.note || "-"}</Typography>
                          {g.sent_at ? (
                              <Typography variant="caption" color="text.secondary">
                                  Sent {handleDateTimeDefault(g.sent_at)}
                              </Typography>
                          ) : null}
                      </TableCell>
                  </TableRow>
              ))
            : null;

    return (
        <Stack spacing={2}>
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ md: "center" }}
            >
                <Typography variant="subtitle1" fontWeight={700}>
                    Unlocked gifts
                    {data?.pending ? (
                        <Chip size="small" color="warning" label={`${data.pending} to send`} sx={{ ml: 1 }} />
                    ) : null}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        select
                        size="small"
                        label="Status"
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            setPagination((p) => ({ ...p, pageNo: 0 }));
                        }}
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="sent">Sent</MenuItem>
                    </TextField>
                    <Box sx={{ minWidth: 240 }}>
                        <SearchRecords handlePagination={(p) => setPagination(p)} pagination={pagination} />
                    </Box>
                </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary">
                MagicLock sends the gift; mark it sent here so the team knows. Search by customer name, mobile or gift.
            </Typography>
            {error ? <Alert severity="error">{error?.data?.message || "Failed to load gifts"}</Alert> : null}
            <TableCommon
                columns={giftColumns}
                tableData={rows}
                count={data?.count || 0}
                loading={isFetching}
                pagination={pagination}
                handlePagination={(p) => setPagination(p)}
                emptyMessage="No gifts unlocked yet"
            />
        </Stack>
    );
};

const MilestonesAdmin = () => (
    <Stack spacing={3}>
        <TiersCard />
        <GiftsCard />
    </Stack>
);

export default MilestonesAdmin;
